import { isMobile } from "../utils";

export class HeaderNav {
  pageHeader: HTMLElement | null = document.getElementById("page-header");
  headerLogo: HTMLElement | null = document.getElementById("header-logo");
  headerContent: HTMLElement | null = document.getElementById("header-content");
  headerMenuToggle: HTMLElement | null =
    document.getElementById("header-menu-toggle");
  headerBackdrop: HTMLElement | null =
    document.getElementById("header-backdrop");
  headerNavContent: HTMLElement | null =
    document.getElementById("header-nav-content");
  newsletterButtons = this.headerNavContent?.querySelectorAll(
    "[data-newsletter-button]",
  ) as NodeListOf<HTMLElement> | null;
  navLinks = this.headerNavContent?.querySelectorAll(
    "a",
  ) as NodeListOf<HTMLElement> | null;

  isMobile = isMobile();

  isNavOpen = false;
  isCompact = true;
  isOpeningNavFromCompact = false;

  constructor() {
    this.initHeaderNav();
  }

  initHeaderNav = () => {
    const headerLogoIntersectionObserver = new IntersectionObserver(
      this.onHeaderLogoIntersection,
    );

    if (this.headerLogo) {
      headerLogoIntersectionObserver.observe(this.headerLogo);
    }

    this.headerMenuToggle?.addEventListener("click", this.toggleNav);

    this.newsletterButtons?.forEach((button) => {
      button.addEventListener("click", () => {
        button.blur();
        this.setOpenState(false);
      });
      this.updateTabIndex(button, false);
    });

    this.navLinks?.forEach((link) => {
      link.addEventListener("click", () => {
        link.blur();
        this.setOpenState(false);
      });
      this.updateTabIndex(link, false);
    });

    this.headerContent?.addEventListener(
      "transitionend",
      this.onHeaderContentTransitionEnd,
    );

    this.headerBackdrop?.addEventListener("click", () =>
      this.setOpenState(false),
    );

    window.addEventListener("keydown", this.onWindowKeyDown);
  };

  onHeaderLogoIntersection = (entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      const isCompact = entry.isIntersecting && !this.isNavOpen;

      this.pageHeader?.classList.toggle("header--compact", isCompact);

      this.isCompact = isCompact;
    });
  };

  toggleNav = () => {
    if (!this.isNavOpen && this.isCompact) {
      this.openNavFromCompact();
      return;
    }

    this.setOpenState(!this.isNavOpen);
  };

  openNavFromCompact = () => {
    if (this.isOpeningNavFromCompact) {
      return;
    }

    const onTransitionEnd = (e: TransitionEvent) => {
      if (e.target === this.headerContent) {
        setTimeout(() => {
          this.isCompact = false;
          this.setOpenState(true);

          this.headerContent?.removeEventListener(
            "transitionend",
            onTransitionEnd,
          );
        }, 180);
      }
    };

    this.headerContent?.addEventListener("transitionend", onTransitionEnd);

    this.pageHeader?.classList.remove("header--compact");
  };

  onHeaderContentTransitionEnd = (e: TransitionEvent) => {
    if (e.target !== this.headerContent) {
      return;
    }

    this.pageHeader?.classList.toggle("header--expanded", !this.isCompact);
  };

  setOpenState = (isOpen: boolean) => {
    this.isNavOpen = isOpen;

    this.pageHeader?.classList.toggle("header--nav-open", this.isNavOpen);
    this.headerMenuToggle?.setAttribute(
      "aria-expanded",
      String(this.isNavOpen),
    );
    this.headerNavContent?.setAttribute("aria-hidden", String(!this.isNavOpen));

    this.newsletterButtons?.forEach((button) => {
      this.updateTabIndex(button, this.isNavOpen);
    });

    this.navLinks?.forEach((link) => {
      this.updateTabIndex(link, this.isNavOpen);
    });
  };

  updateTabIndex = (element: HTMLElement, visible: boolean) => {
    if (visible) {
      element.removeAttribute("tabindex");
    } else {
      element.setAttribute("tabindex", "-1");
    }
  };

  onWindowKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape" && this.isNavOpen) {
      this.setOpenState(false);
    }
  };
}
